import { LabeledStatus, Tab, TabsNavigation } from 'components/molecules';
import Buttons from 'components/molecules/buttons';

import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import { FormObserver } from 'hooks/useFormObserver';
import i18next from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { IArtistMutationForm } from 'services/artists/types';
import { STATUS } from 'services/types';
import { II18nextPrefix } from 'types/components/components.interface';
import {
  conditionalField,
  externalIdsValidation,
  getTabError,
  requiredEnumSelect,
} from 'utils/global/validation.utils';
import { array, bool, number, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import ArtistBand, { artistsInBandSchema } from './tabs/ArtistBand';
import ArtistData from './tabs/ArtistData';

export enum ArtistTabs {
  data,
  band,
  external,
  bio,
}

export const artistInitialValues: IArtistMutationForm = {
  id: '',
  email: '',
  firstName: '',
  isBand: false,
  isBandOption: 0,
  labelId: '',
  labelName: '',
  lastName: '',
  nickname: '',
  phoneNumber: {
    number: '',
    prefix: '',
  },
  remarks: '',
  statusId: STATUS.draft,
  suffix: '',

  profilePictureLink: '',
  profilePicturePreviewLink: '',
  artistsInBand: [],

  prefix: '',
  version: 0,
  externalIds: [],
  file: null as any,
  fileOriginal: null as any,
};

export const artistPhoneNumberValidator = object({
  prefix: string()
    .nullable()
    .test('prefix-required-if-number', i18next.t('formik-error.required'), function (value) {
      const { number } = this.parent;
      const hasNumber = typeof number === 'string' && number.trim() !== '';
      const hasPrefix = typeof value === 'string' && value.trim() !== '';
      if (hasNumber && !hasPrefix) {
        return this.createError({
          path: this.path,
          message: i18next.t('formik-error.required'),
        });
      }
      return true;
    }),
  number: string()
    .nullable()
    .test('number-required-if-prefix', i18next.t('formik-error.required'), function (value) {
      const { prefix } = this.parent;
      const hasPrefix = typeof prefix === 'string' && prefix.trim() !== '';
      const hasNumber = typeof value === 'string' && value.trim() !== '';
      if (hasPrefix && !hasNumber) {
        return this.createError({
          path: this.path,
          message: i18next.t('formik-error.required'),
        });
      }
      return true;
    }),
});

export const artistValidator = object().shape({
  isBandOption: number().required(() => i18next.t('formik-error.required')),
  isBand: bool().required(() => i18next.t('formik-error.required')),
  statusId: requiredEnumSelect(STATUS),
  firstName: string(),
  lastName: string(),
  nickname: string().required(() => i18next.t('formik-error.required')),
  email: string()
    .email(() => i18next.t('formik-error.invalid-email'))
    .optional()
    .nullable(),
  phoneNumber: artistPhoneNumberValidator,
  prefix: string().nullable(),
  suffix: string().nullable(),
  labelId: string().nullable(),
  labelName: string(),
  remarks: string().nullable(),
  externalIds: array().of(externalIdsValidation),
  artistsInBand: conditionalField(
    conditionalField(
      array()
        .of(artistsInBandSchema)
        .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
        .required(() => i18next.t('formik-error.required')),
      'isBandOption',
      [0],
      array().of(artistsInBandSchema),
    ),
    'statusId',
  ),
});

export interface IArtistForm
  extends FormikConfig<IArtistMutationForm>, IFormProps, II18nextPrefix {}

const ArtistForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  initialValues = artistInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  status,
  ...props
}: IArtistForm) => {
  const { t } = useTranslation();

  const { setForm } = useStoreState();

  return (
    <Formik
      {...props}
      validationSchema={artistValidator}
      validateOnChange={true}
      validateOnBlur={true}
      validateOnMount
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}
    >
      {({ values, status, errors }: IFormikProps<IArtistMutationForm>) => (
        <Form>
          <FormObserver<IArtistMutationForm>
            onChange={(newValues) => {
              setForm('artistForm', newValues);
            }}
          />
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={tittle}
              path={backPath ?? 'artist'}
              topbarContent={
                <div className="flex justify-end">
                  <div className="flex items-center gap-4 justify-end">
                    {/* {!!initialValues.nickname.length && status?.displayMode && (
                      <LabeledStatus status={initialValues.statusId} noname />
                    )} */}
                    {!status?.displayMode && <Buttons.FormSubmit />}
                    {topbarContent}
                  </div>
                </div>
              }
            >
              <TabsNavigation
                tabs={[
                  {
                    value: ArtistTabs.data,
                    name: 'data',
                    error: getTabError([
                      errors.nickname,
                      errors.firstName,
                      errors.lastName,
                      errors.prefix,
                      errors.suffix,
                      errors.isBand,
                      errors.email,
                    ]),
                  },
                  ...(values.isBand
                    ? [
                        {
                          value: ArtistTabs.band,
                          name: 'band',
                          error: errors.artistsInBand,
                        },
                      ]
                    : []),
                  {
                    value: ArtistTabs.external,
                    name: 'external',
                    error: errors.externalIds,
                  },
                  // {
                  //   value: ArtistTabs.bio,
                  //   name: 'bio',
                  // },
                ]}
                disabled={disableTabs}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

ArtistForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'data', element: <ArtistData /> },
    { path: 'external', element: <Tab.ExternalId parent="ARTIST" /> },
    { path: 'band', element: <ArtistBand /> },
  ].filter((children) => !disabledChildren?.includes(children.path));

export default ArtistForm;
